import { create } from 'zustand';

import { isValidJSON } from '@/helpers/common';

// TODO: use persist from 'zustand/middleware/persist';
export interface CommonLocalStorageState {
  localStorageData: Record<string, any>;

  get: <T>(key: string, defaultValue?: T, isPlainString?: boolean) => T;
  push: <T>(key: string, object: T) => void;
  replace: <T>(key: string, object: T) => void;
  remove: (key: string) => void;
}

export enum STORAGE {
  LAST_SEEN_EMAIL_CONFIRMATION_POPUP = 'last-seen-email-confirmation-popup',
  HAS_SHOWN_LIMIT_MODAL = 'has-shown-limit-modal',
  USER_DATA = 'user-data',
  USER_ID = 'userId',
  ANALYTICS = 'analytics',
  CUSTOM_OFFER_LOCAL_STORAGE_KEY = 'custom-offers',
  HAS_SHOWN_CONFETTI = 'has-shown-confetti',
  PAYMENTS = 'payments',
  APPROVE_LANGUAGE_DATE = 'approveLanguageDate',
  TOURNAMENT_BANNER_CLOSED = 'tournament-banner_closed',
  IS_STAG = 'isStag',
  STAG = 'stag',
  SPECIAL_STAG = 'specialStag',
  BONUS_BONANZA_CLICK = 'bonus-bonanza-click',
  IS_SEARCH_ENGINE = 'is-search-engine',
  NDB_FLOW = 'ndb-flow',
  MISSION = 'mission',
  GOOGLE_OAUTH_AFFILIATE_PARAMS = 'google-oauth-affiliate-params',
  NDB_LANDING_CODE = 'ndb-landing-code',
}

export const useLocalStorageStore = create<CommonLocalStorageState>()(
  (set, getState) => ({
    localStorageData: {
      [STORAGE.LAST_SEEN_EMAIL_CONFIRMATION_POPUP]: null,
      [STORAGE.HAS_SHOWN_LIMIT_MODAL]: null,
      [STORAGE.USER_DATA]: null,
      [STORAGE.USER_ID]: null,
      [STORAGE.ANALYTICS]: null,
      [STORAGE.CUSTOM_OFFER_LOCAL_STORAGE_KEY]: null,
      [STORAGE.HAS_SHOWN_CONFETTI]: null,
      [STORAGE.PAYMENTS]: null,
      [STORAGE.TOURNAMENT_BANNER_CLOSED]: null,
      [STORAGE.IS_STAG]: false,
      [STORAGE.STAG]: null,
      [STORAGE.SPECIAL_STAG]: null,
      [STORAGE.BONUS_BONANZA_CLICK]: null,
      [STORAGE.IS_SEARCH_ENGINE]: false,
      [STORAGE.NDB_FLOW]: null,
      [STORAGE.MISSION]: null,
      [STORAGE.GOOGLE_OAUTH_AFFILIATE_PARAMS]: null,
      [STORAGE.NDB_LANDING_CODE]: null,
    },
    get: <T>(key: string, defaultValue?: T, isPlainString?: boolean): T => {
      try {
        const item = localStorage.getItem(key);
        if (item == null) {
          if (defaultValue) {
            set((state: CommonLocalStorageState) => ({
              ...state,
              localStorageData: {
                ...state.localStorageData,
                [key]: defaultValue,
              },
            }));
            return defaultValue;
          }
          return getState().localStorageData[key];
        }

        if (!isPlainString && isValidJSON(item)) return JSON.parse(item) as T;
        return item as T;
      } catch {
        return getState().localStorageData[key];
      }
    },
    push: <T>(key: string, object: T) => {
      const oldObject = getState().localStorageData[key];
      const newObject = Array.isArray(oldObject)
        ? [...oldObject, ...(Array.isArray(object) ? object : [object])]
        : typeof oldObject === 'object' && oldObject !== null
          ? { ...oldObject, ...object }
          : object;

      const newValue =
        typeof newObject === 'string' ? newObject : JSON.stringify(newObject);

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(key, newValue);
      }

      set((state: CommonLocalStorageState) => ({
        ...state,
        localStorageData: { ...state.localStorageData, [key]: newValue },
      }));
    },
    replace: <T>(key: string, object: T) => {
      const newValue =
        typeof object === 'string' ? object : JSON.stringify(object);

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(key, newValue);
      }

      set((state: CommonLocalStorageState) => ({
        ...state,
        localStorageData: { ...state.localStorageData, [key]: object },
      }));
    },
    remove: (key: string) => {
      set((state: CommonLocalStorageState) => {
        localStorage.removeItem(key);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [key]: _, ...newLocalStorageData } = state.localStorageData;

        return { ...state, localStorageData: newLocalStorageData };
      });
    },
  })
);
